import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Spin,
  message,
  Divider,
  Space,
  Row,
  Col,
  Typography,
  Card,
  Empty,
  Tooltip,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';

const KeyValuePairEditor = ({ value, onChange }) => {
  const [keyValuePairs, setKeyValuePairs] = useState([]);
  const { Text } = Typography;

  useEffect(() => {
    let data;
    try {
      data = typeof value === 'string' ? JSON.parse(value) : value;
    } catch (err) {
      data = {};
    }

    if (data) {
      if (typeof data === 'object' && !Array.isArray(data)) {
        setKeyValuePairs(
          Object.entries(data).map(([key, val]) => ({
            key,
            value:
              typeof val === 'object' ? JSON.stringify(val) : val.toString(),
            isObject: typeof val === 'object' && val !== null,
          }))
        );
      } else if (Array.isArray(data)) {
        setKeyValuePairs(
          data.map((item, index) => ({
            key: index.toString(),
            value:
              typeof item === 'object' ? JSON.stringify(item) : item.toString(),
            isObject: typeof item === 'object' && item !== null,
          }))
        );
      } else {
        // Handle primitive
        setKeyValuePairs([
          { key: '', value: data.toString(), isObject: false },
        ]);
      }
    } else {
      setKeyValuePairs([{ key: '', value: '', isObject: false }]);
    }
  }, [value]);

  const handleKeyValueChange = (index, field, newValue) => {
    const updatedPairs = [...keyValuePairs];
    updatedPairs[index][field] = newValue;
    setKeyValuePairs(updatedPairs);

    updateParentData(updatedPairs);
  };

  const updateParentData = (pairs) => {
    const isArray = pairs.every((pair) => !isNaN(Number(pair.key)));
    let result;

    if (isArray) {
      // Create array
      result = [];
      pairs.forEach((pair) => {
        const index = Number(pair.key);
        try {
          // Try to parse JSON
          result[index] = JSON.parse(pair.value);
        } catch {
          // Use as string if not valid JSON
          result[index] = pair.value;
        }
      });

      // Filter out undefined entries
      result = result.filter((item) => item !== undefined);
    } else {
      // Create object
      result = {};
      pairs.forEach((pair) => {
        if (pair.key.trim()) {
          try {
            // Try to parse JSON
            result[pair.key] = JSON.parse(pair.value);
          } catch {
            // Use as string if not valid JSON
            result[pair.key] = pair.value;
          }
        }
      });
    }

    onChange(JSON.stringify(result));
  };

  const addKeyValuePair = () => {
    setKeyValuePairs([
      ...keyValuePairs,
      { key: '', value: '', isObject: false },
    ]);
  };

  const removeKeyValuePair = (index) => {
    if (keyValuePairs.length > 1) {
      const updatedPairs = keyValuePairs.filter((_, i) => i !== index);
      setKeyValuePairs(updatedPairs);
      updateParentData(updatedPairs);
    }
  };

  if (keyValuePairs.length === 0) {
    return <Empty description="No data" />;
  }

  return (
    <div className="key-value-pair-editor">
      {keyValuePairs.map((pair, index) => (
        <Card
          key={index}
          size="small"
          style={{ marginBottom: 8 }}
          bodyStyle={{ padding: '8px' }}
        >
          <Row gutter={8} align="middle">
            <Col span={7}>
              <Input
                placeholder="Key"
                value={pair.key}
                onChange={(e) =>
                  handleKeyValueChange(index, 'key', e.target.value)
                }
                size="middle"
              />
            </Col>
            <Col span={15}>
              <Input.TextArea
                rows={5}
                placeholder="Value"
                value={pair.value}
                onChange={(e) =>
                  handleKeyValueChange(index, 'value', e.target.value)
                }
                size="middle"
              />
            </Col>
            <Col span={2}>
              <Space>
                <Tooltip title="Remove">
                  <Button
                    icon={<DeleteOutlined />}
                    onClick={() => removeKeyValuePair(index)}
                    danger
                    disabled={keyValuePairs.length === 1}
                    size="small"
                  />
                </Tooltip>
              </Space>
            </Col>
          </Row>
        </Card>
      ))}

      <Button
        type="dashed"
        onClick={addKeyValuePair}
        icon={<PlusOutlined />}
        style={{ marginTop: 8, width: '100%' }}
        size="middle"
      >
        Add Field
      </Button>
    </div>
  );
};

export default KeyValuePairEditor;
